// Sections. A card is a white panel with a hairline edge on the paper ground; soft is the quiet panel inside one.
import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { cx } from '@/lib/cx';
import { Icon, type IconName } from './Icon';

type CardProps = {
  children: ReactNode;
  /** soft: the quiet panel, for something shown in place (a hint, a fix) */
  tone?: 'plain' | 'soft';
  /** Vertical rhythm for direct children: sm 8px · md 12px · lg 24px. */
  stack?: 'sm' | 'md' | 'lg';
  as?: 'div' | 'section' | 'article' | 'aside';
  className?: string;
  id?: string;
};

export function Card({ children, tone = 'plain', stack, as: Tag = 'div', className, id }: CardProps) {
  const stackCls = stack === 'sm' ? 'stack-sm' : stack === 'lg' ? 'stack-lg' : stack === 'md' ? 'stack' : undefined;
  return (
    <Tag id={id} className={cx('card', tone !== 'plain' && tone, stackCls, className)}>
      {children}
    </Tag>
  );
}

/** A lead statement at the top of a page (Progress: "You improved in every skill"). */
export function Hero({ children }: { children: ReactNode }) {
  return <article className="hero stack">{children}</article>;
}

type LinkCardProps = {
  to: string;
  icon: IconName;
  title: string;
  sub?: ReactNode;
};

/** A row that links somewhere, under a hairline: icon, title in bold, one line. */
export function LinkCard({ to, icon, title, sub }: LinkCardProps) {
  return (
    <Link className="item-card" to={to}>
      <Icon name={icon} />
      <span className="grow">
        <span className="title">{title}</span>
        {sub && <span className="sub">{sub}</span>}
      </span>
    </Link>
  );
}

export function Divider() {
  return <hr className="divider" />;
}
